import { Badge } from "@veritrellis/ui";
import { Link } from "react-router-dom";
import { CopyButton } from "../CopyButton.js";
import {
  auditCategoryAccent,
  auditCategoryLabel,
  auditEventBadgeVariant,
  categorizeAuditEvent,
  formatAuditEventTitle,
  formatDateTime,
  type AuditEvent
} from "../../lib/auditPresentation.js";
import { actorBadgeVariant, envLabel, formatActorLabel } from "../../lib/requestPresentation.js";

type AuditEventInspectorProps = {
  event: AuditEvent | null;
};

export const AuditEventInspector = ({ event }: AuditEventInspectorProps) => {
  if (event === null) {
    return (
      <section className="admin-panel admin-audit-inspector" aria-label="Event details">
        <div className="admin-audit-inspector-empty">
          <p className="admin-audit-inspector-empty-title">Event inspector</p>
          <p className="admin-form-field-hint">
            Select an event from the timeline to view actor context, correlation IDs, and the full
            payload.
          </p>
        </div>
      </section>
    );
  }

  const category = categorizeAuditEvent(event.event_type);
  const accent = auditCategoryAccent[category];

  return (
    <section className="admin-panel admin-audit-inspector" aria-label="Event details">
      <div className="admin-panel-header">
        <h2 className="admin-panel-title">Event details</h2>
        <span
          className="admin-audit-category-chip"
          style={{ borderColor: accent, color: accent, background: `${accent}14` }}
        >
          {auditCategoryLabel[category]}
        </span>
      </div>

      <div className="admin-audit-inspector-body">
        <div className="admin-audit-inspector-hero">
          <Badge variant={auditEventBadgeVariant(event.event_type)} className="normal-case">
            {event.event_type}
          </Badge>
          <h3 className="admin-audit-inspector-event-title">
            {formatAuditEventTitle(event.event_type)}
          </h3>
          <time className="admin-form-field-hint" dateTime={event.created_at}>
            {formatDateTime(event.created_at)} · {envLabel(event.environment)}
          </time>
        </div>

        <dl className="admin-audit-meta-grid">
          <div>
            <dt className="admin-meta-label">Actor</dt>
            <dd className="admin-meta-value">
              <Badge variant={actorBadgeVariant(event.actor.type)} className="normal-case">
                {formatActorLabel(event.actor)}
              </Badge>
              {event.actor.type === "user" && event.actor.email != null ? (
                <p className="admin-form-field-hint" style={{ marginTop: 6 }}>
                  {event.actor.email}
                </p>
              ) : null}
              {event.actor.type !== "system" && event.actor.id != null ? (
                <div className="admin-audit-id-row">
                  <code>{event.actor.id}</code>
                  <CopyButton value={event.actor.id} label="Copy actor ID" />
                </div>
              ) : null}
            </dd>
          </div>

          <div>
            <dt className="admin-meta-label">Event ID</dt>
            <dd className="admin-meta-value">
              <div className="admin-audit-id-row">
                <code>{event.id}</code>
                <CopyButton value={event.id} label="Copy event ID" />
              </div>
            </dd>
          </div>

          {event.request_id != null ? (
            <div>
              <dt className="admin-meta-label">Request</dt>
              <dd className="admin-meta-value">
                <div className="admin-audit-id-row">
                  <Link to={`/requests/${event.request_id}`} className="admin-inbox-item-link">
                    View request
                  </Link>
                </div>
                <div className="admin-audit-id-row" style={{ marginTop: 6 }}>
                  <code>{event.request_id}</code>
                  <CopyButton value={event.request_id} label="Copy request ID" />
                </div>
              </dd>
            </div>
          ) : null}

          {event.permit_id != null ? (
            <div>
              <dt className="admin-meta-label">Permit</dt>
              <dd className="admin-meta-value">
                <div className="admin-audit-id-row">
                  <code>{event.permit_id}</code>
                  <CopyButton value={event.permit_id} label="Copy permit ID" />
                </div>
              </dd>
            </div>
          ) : null}
        </dl>

        <div>
          <p className="admin-meta-label" style={{ marginBottom: 8 }}>
            Payload
          </p>
          <pre className="admin-code-block admin-audit-payload">
            {JSON.stringify(event.data ?? {}, null, 2)}
          </pre>
        </div>
      </div>
    </section>
  );
};
